import {
  LifeTransactionType,
  lifeTransactionTypeMap,
} from '@tymbe/schema/enums';
import { use, useCallback, useRef } from 'react';
import { ModalContext } from './index.page';
import { TyTimelineItemButton } from './TyTimelineItemButton';
import { DotColorEnum } from '@tymbe/ui-primitives'
import { TyTimelineItem, TyTimelineItemProps } from './TyTimelineItem';
import moment from 'moment';
import { useLifeTransactions } from '../components/useLifeTransactions';
import { useParams } from 'react-router-dom';
import Spinner from '@tymbe/ty-components/indicators/Spinner';

const getTitle = (numberOfLives: number) => {
  let lifeForm = 'životů';
  const absoluteLives = Math.abs(numberOfLives);
  if (absoluteLives === 1) {
    lifeForm = 'život';
  }
  if (absoluteLives >= 2 && absoluteLives <= 4) {
    lifeForm = 'životy';
  }

  const sign = numberOfLives > 0 ? '+' : '-';

  return `${sign}${absoluteLives} ${lifeForm}`;
};

export const TyTimeline = () => {
  const { toggleAttendanceDetail } = use(ModalContext);
  const { personId } = useParams();
  const { isFetching, hasNextPage, fetchNextPage, isError, data, isSuccess } =
    useLifeTransactions(personId!);

  const observer = useRef<IntersectionObserver | null>(null);

  const lastItemObserverRef = useCallback(
    (node: HTMLElement) => {
      if (isFetching) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        async (entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            await fetchNextPage();
          }
        },
        {
          threshold: 0,
        }
      );
      // Re-scan, find and observe the new node.
      if (node) observer.current.observe(node);
    },
    [isFetching, hasNextPage]
  );

  // Fetch failed
  if (isError) {
    return (
      <div className="flex items-center justify-center mb-5">
        Ups, něco se pokazilo :(
      </div>
    );
  }

  // Initial fetch where we don't have any data yet
  if (data?.pages === undefined && isFetching) {
    return (
      <div className="flex items-center justify-center mb-5">
        <Spinner className="w-[20px] h-[20px]" />
      </div>
    );
  }

  // No data found
  if (isSuccess && data?.pages[0].total === 0) {
    return (
      <div className="flex items-center justify-center mb-5">
        Nenalezeny žádné záznamy
      </div>
    );
  }

  return (
    <>
      {isFetching ? (
        <div className="flex flex-row items-center justify-center mb-5 gap-2">
          <Spinner  className="w-[20px] h-[20px]" /> Data se
          aktualizují
        </div>
      ) : (
        ''
      )}

      <ol className="relative border-s-2 border-fg-100 pl-10 userInfo">
        <div className="absolute h-[6px] w-[6px] bg-fg-100 transform -left-[4px] rounded-full" />
        <div className="absolute h-[6px] w-[6px] bg-fg-100 transform -left-[4px] bottom-0 rounded-full" />

        {data?.pages.map((page) =>
          page.data.map((lifeTransaction, i) => {
            const hasApplication = [
              LifeTransactionType.APPROVED_EXCUSE,
              LifeTransactionType.CANCELED_LATE_SHIFT,
              LifeTransactionType.UNEXCUSED_ABSENCE,
            ].includes(lifeTransaction.type);
            const title = getTitle(lifeTransaction.value);
            const note = `[${lifeTransactionTypeMap[lifeTransaction.type]}] ${
              lifeTransaction.note ? lifeTransaction.note : 'Bez poznámky'
            }`;
            const dotColor =
              lifeTransaction.value > 0 ? DotColorEnum.green : DotColorEnum.red;
            const dateCreated = moment(lifeTransaction.created_at).format(
              'DD. MM. YYYY, HH:mm'
            );

            const createdBy = lifeTransaction.creator
              ? `[${lifeTransaction.creator?.id}] ${
                  lifeTransaction.creator?.first_name || ''
                } ${lifeTransaction.creator?.middle_name || ''}${
                  lifeTransaction.creator?.last_name || ''
                }`
              : 'Tymbe';
            const action = hasApplication ? (
              <TyTimelineItemButton
                onClickHandler={() =>
                  toggleAttendanceDetail(lifeTransaction.application ?? null)
                }
              >
                Zobrazit směnu
              </TyTimelineItemButton>
            ) : undefined;

            const lastItemObserverRefProp =
              i + 1 === page.data.length ? lastItemObserverRef : null;

            const tyTimelineItemProps: TyTimelineItemProps = {
              dotColor,
              title,
              note,
              createdBy,
              action,
              dateCreated,
              lastItemObserverRef: lastItemObserverRefProp,
            };

            return (
              <TyTimelineItem
                key={lifeTransaction.id}
                {...tyTimelineItemProps}
              />
            );
          })
        )}
      </ol>
      {/* 
      // Fetching next page 
      */}
      {isFetching && (
        <div className="flex items-center justify-center mb-5">
          <Spinner className="w-[20px] h-[20px]" />
        </div>
      )}
    </>
  );
};
